// src/context/MeetingContext.jsx
// Fixed: was ordering by "startTime" (field doesn't exist — meetings use "time").
// Now uses useTodayMeetings from useFirestore which correctly:
//   • fetches only today's date
//   • orders by "time" (the real field)
//   • auto-expires meetings via 60s tick without refresh

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  startAfter,
  updateDoc,
  doc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useTodayMeetings, calculateEndTime, getTodayStr } from "../hooks/useFirestore";

// ── Context ────────────────────────────────────
const MeetingContext = createContext(null);

export function useMeeting() {
  const ctx = useContext(MeetingContext);
  if (!ctx) throw new Error("useMeeting must be used inside <MeetingProvider>");
  return ctx;
}

// ── Provider ───────────────────────────────────
export function MeetingProvider({ children }) {
  // ── Today's meetings — uses the corrected hook ──
  // activeMeetings = not yet expired; allTodayMeetings = incl. expired
  const {
    data: activeMeetings,
    allTodayMeetings,
    loading: todayMeetingsLoading,
  } = useTodayMeetings();

  // Keep legacy names for components that use them
  const todayMeetings = activeMeetings;
  const upcomingMeetings = activeMeetings;

  // ── All meetings — paginated, for admin/lead scheduling pages ──
  const [allMeetings, setAllMeetings] = useState([]);
  const [allMeetingsLoading, setAllMeetingsLoading] = useState(false);
  const [allMeetingsHasMore, setAllMeetingsHasMore] = useState(true);
  const [allMeetingsLastDoc, setAllMeetingsLastDoc] = useState(null);

  // ── User-specific meetings (participant filter) ──
  const [userMeetings, setUserMeetings] = useState([]);
  const [userMeetingsLoading, setUserMeetingsLoading] = useState(true);

  // ── Load all meetings (paginated, one-time fetch) ──
  const loadAllMeetings = useCallback(
    async (reset = false) => {
      if (reset) {
        setAllMeetings([]);
        setAllMeetingsLastDoc(null);
        setAllMeetingsHasMore(true);
      }
      setAllMeetingsLoading(true);

      let q = query(
        collection(db, "meetings"),
        orderBy("date", "desc"),
        limit(20),
      );
      if (!reset && allMeetingsLastDoc) {
        q = query(q, startAfter(allMeetingsLastDoc));
      }

      try {
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Client-side sub-sorting by time desc for consistent display
        data.sort((a, b) => {
          const c = (b.date || "").localeCompare(a.date || "");
          if (c !== 0) return c;
          return (b.time || "").localeCompare(a.time || "");
        });

        if (reset) {
          setAllMeetings(data);
        } else {
          setAllMeetings((prev) => [...prev, ...data]);
        }
        setAllMeetingsLastDoc(snap.docs[snap.docs.length - 1] || null);
        setAllMeetingsHasMore(snap.docs.length === 20);
      } catch (error) {
        console.error("Error loading all meetings:", error);
      } finally {
        setAllMeetingsLoading(false);
      }
    },
    [allMeetingsLastDoc],
  );

  // ── User meetings — today only, for participants ──
  const subscribeToUserMeetings = useCallback((userId) => {
    if (!userId) {
      setUserMeetings([]);
      setUserMeetingsLoading(false);
      return () => {};
    }
    setUserMeetingsLoading(true);
    const today = getTodayStr();

    // Query simple where filter to completely avoid composite index failed-precondition errors
    const q = query(
      collection(db, "meetings"),
      where("date", "==", today)
    );

    const unsub = onSnapshot(q, (snap) => {
      const now = new Date();
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (a.time || "").localeCompare(b.time || ""));

      const data = docs
        .map((m) => {
          const endTimeStr =
            m.endTime ||
            (m.duration && m.time ? calculateEndTime(m.time, m.duration) : m.time);
          return { ...m, _endTimeStr: endTimeStr };
        })
        // Filter in memory for user participation/audience visibility
        .filter((m) => {
          // If in participants list
          if (m.participants && Array.isArray(m.participants) && m.participants.includes(userId)) {
            return true;
          }
          // If scheduled by user
          if (m.scheduledBy === userId) return true;
          // If audience is all
          const aud = (m.audience || "").toLowerCase();
          if (aud === "all") return true;
          
          return false;
        })
        // Only show not-yet-expired for user
        .filter((m) =>
          m.date && m._endTimeStr
            ? new Date(`${m.date}T${m._endTimeStr}`) > now
            : true,
        );
      setUserMeetings(data);
      setUserMeetingsLoading(false);
    }, (err) => {
      console.error("[subscribeToUserMeetings]", err);
      setUserMeetingsLoading(false);
    });

    return unsub;
  }, []);

  // ── Optimistic join ──
  const optimisticJoinMeeting = useCallback(async (meetingId, userId) => {
    try {
      await updateDoc(doc(db, "meetings", meetingId), {
        attendees: arrayUnion(userId),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("joinMeeting error:", error);
      throw error;
    }
  }, []);

  // ── Optimistic leave ──
  const optimisticLeaveMeeting = useCallback(async (meetingId, userId) => {
    try {
      await updateDoc(doc(db, "meetings", meetingId), {
        attendees: arrayRemove(userId),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("leaveMeeting error:", error);
      throw error;
    }
  }, []);

  // ── Context value ────────────────────────────
  const value = React.useMemo(
    () => ({
      // Today's active (not-yet-expired) meetings — primary dashboard source
      todayMeetings,
      todayMeetingsLoading,

      // Alias for backward compat
      upcomingMeetings,
      upcomingLoading: todayMeetingsLoading,

      // All today's meetings incl. expired (for meeting detail pages)
      allTodayMeetings,

      // All meetings — paginated
      allMeetings,
      allMeetingsLoading,
      allMeetingsHasMore,
      loadAllMeetings,

      // User (participant) meetings
      userMeetings,
      userMeetingsLoading,
      subscribeToUserMeetings,

      // Write operations
      joinMeeting: optimisticJoinMeeting,
      leaveMeeting: optimisticLeaveMeeting,
    }),
    [
      todayMeetings,
      todayMeetingsLoading,
      upcomingMeetings,
      allTodayMeetings,
      allMeetings,
      allMeetingsLoading,
      allMeetingsHasMore,
      loadAllMeetings,
      userMeetings,
      userMeetingsLoading,
      subscribeToUserMeetings,
      optimisticJoinMeeting,
      optimisticLeaveMeeting,
    ],
  );

  return (
    <MeetingContext.Provider value={value}>{children}</MeetingContext.Provider>
  );
}
